// CSS: Tailwind/Flowbite last so layout utilities win.
import '@fortawesome/fontawesome-free/css/all.min.css'
import 'vue-toastification/dist/index.css'
import '@/assets/css/tailwind.css'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import Toast, { POSITION, type PluginOptions } from 'vue-toastification'
import App from './App.vue'

import { createAppRouter } from '@/router'
import i18n from '@/core/plugins/i18n'
import { initVeeValidate } from '@/core/plugins/vee-validate'
import * as ApiService from '@/core/services/ApiService'
import { initBrand } from '@/utils/brand'
import {
  clearChunkReloadAttempt,
  isStaleChunkError,
  reloadOnceForStaleChunks
} from '@/utils/chunkLoadRecovery'
import { initSentry } from '@/services/sentry'
import { zustandPersistPlugin } from '@/stores/persist/zustandCompat'
import { logError } from '@/utils/safeErrorLog'

// Before the router reads the URL: initBrand strips the /hs and ?hs testing markers and sets
// <html data-brand> and the favicon.
initBrand()

// Stale hashed chunks after deploy are left to reject: route loads recover in router.onError
// (onto the clicked route), anything else here. Never preventDefault `vite:preloadError` —
// Vite then resolves the import to undefined and vue-router throws "Couldn't resolve component".
window.addEventListener('unhandledrejection', (event) => {
  if (isStaleChunkError(event.reason)) {
    event.preventDefault()
    reloadOnceForStaleChunks()
    return
  }
  // Sentry's global handlers capture unhandled rejections themselves.
  logError('[unhandled-rejection]', event.reason)
})

const app = createApp(App)

app.config.errorHandler = (err, instance, info) => {
  logError(`[vue-error] ${info} ${instance?.$options?.name ?? ''}`, err)
}

// Pinia; stores with a `zustandPersist` option persist in the React portal's format.
const pinia = createPinia()
pinia.use(zustandPersistPlugin)
app.use(pinia)

const router = createAppRouter()
app.use(router)
// After errorHandler is set (Sentry wraps it) and the router is installed (tracing).
initSentry(app, router)

const toastOptions: PluginOptions = {
  position: POSITION.TOP_CENTER
}
app.use(Toast, toastOptions)

// PatientPortalApi client: base URL, 30 s timeout, bearer token and 401 sign-out interceptors.
ApiService.init()
initVeeValidate()

app.use(i18n)

app.mount('#app')

void router
  .isReady()
  .then(() => {
    clearChunkReloadAttempt()
  })
  // A failed first navigation (e.g. a stale route chunk) is already handled by router.onError;
  // letting this rejection go unhandled would also fire the unhandledrejection recovery.
  .catch(() => {})
